/**
 * Sprint V102.j (So 27.09.2026) — MarketingBanner
 * ---------------------------------------------------------------------------
 * Wiederverwendbarer Top-Banner fuer Marketing-Kampagnen. Wird in
 * /pricing, /cockpit und allen /dashboard/*-Routen oberhalb der Fold
 * gerendert. Konfiguration kommt vom Backend (/api/marketing/banner/active).
 *
 * Rendert nichts, wenn:
 *  - Kein aktiver Banner fuer den User
 *  - User hat den Banner bereits dismissed (persistent per Mongo)
 *  - Anonyme User (kein useAuth().user)
 */
import React, { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BG_MAP = {
  amber: "bg-amber-50 border-amber-200 text-amber-950",
  blue: "bg-blue-50 border-blue-200 text-blue-950",
  slate: "bg-slate-50 border-slate-200 text-slate-950",
  orange: "bg-orange-50 border-orange-200 text-orange-950",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-950",
};

const CTA_BG_MAP = {
  amber: "bg-amber-600 hover:bg-amber-700",
  blue: "bg-blue-700 hover:bg-blue-800",
  slate: "bg-slate-700 hover:bg-slate-800",
  orange: "bg-orange-600 hover:bg-orange-700",
  emerald: "bg-emerald-600 hover:bg-emerald-700",
};

export function MarketingBanner({ className = "" }) {
  const { user } = useAuth();
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || user === false || !user.email) return;
    let cancelled = false;
    api
      .get("/marketing/banner/active")
      .then((r) => {
        if (!cancelled) setBanner(r.data?.banner || null);
      })
      .catch(() => {
        if (!cancelled) setBanner(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleDismiss = async () => {
    if (!banner?.key) return;
    setDismissed(true);
    try {
      await api.post("/marketing/banner/dismiss", { key: banner.key });
    } catch {
      /* silent — Dismiss ist idempotent */
    }
  };

  if (!user || user === false || !user.email) return null;
  if (!banner || dismissed) return null;

  const bgClass = BG_MAP[banner.bg_color] || BG_MAP.amber;
  const ctaClass = CTA_BG_MAP[banner.bg_color] || CTA_BG_MAP.amber;

  return (
    <div
      data-testid={`marketing-banner-${banner.key}`}
      className={`w-full border rounded-xl shadow-sm ${bgClass} ${className}`}
    >
      <div className="max-w-6xl mx-auto px-4 py-3 sm:px-6 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <p
            data-testid={`marketing-banner-${banner.key}-headline`}
            className="font-bold text-sm sm:text-base leading-snug"
          >
            {banner.headline}
          </p>
          {banner.subline ? (
            <p
              data-testid={`marketing-banner-${banner.key}-subline`}
              className="mt-1 text-xs sm:text-sm opacity-90 leading-snug"
            >
              {banner.subline}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0 self-stretch sm:self-auto">
          <a
            data-testid={`marketing-banner-${banner.key}-cta`}
            href={banner.cta_url}
            className={`inline-flex items-center gap-1.5 ${ctaClass} text-white px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors`}
          >
            {banner.cta_label}
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
          {banner.dismissible ? (
            <button
              type="button"
              data-testid={`marketing-banner-${banner.key}-dismiss`}
              onClick={handleDismiss}
              className="p-1.5 rounded-md hover:bg-black/5 transition-colors"
              aria-label="Banner schließen"
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default MarketingBanner;
